/**
 * useApiClient Hook
 * Multi-Tenant API Client Hook for React Components
 *
 * @description Issue #283 対応 - AuthServiceとの統合型安全フック
 * @backend NestJS Multi-Tenant API統合
 * @security エンタープライズレベル認証統合
 */

import { useState, useCallback, useMemo } from 'react';
import AuthService from '../services/authService';
import logger from '../utils/logger';
import { useErrorContext } from '../contexts/ErrorContext';
import { handleError, EnhancedApiError } from '../utils/error';

// Multi-Tenant Types
import type {
  UserInfo,
  TenantInfo,
  TenantContext,
  TenantResource,
  TenantPermissionActions,
  PlanFeature,
} from '../types/multiTenant';

// API Client State Interface
export interface ApiClientState {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: UserInfo | null;
  tenantContext: TenantContext | null;
  error: EnhancedApiError | null;
}

// API Client Actions Interface
export interface ApiClientActions {
  // Authentication
  login: (email: string, password: string, tenantSubdomain?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;

  // User Management
  getCurrentUser: () => UserInfo | null;
  updateProfile: (data: Partial<UserInfo>) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;

  // Tenant Operations
  getCurrentTenant: () => Promise<TenantInfo | null>;
  updateTenantSettings: (settings: unknown) => Promise<void>;
  getTenantAnalytics: () => Promise<any>;

  // Permission & Security
  hasPermission: (resource: TenantResource, action: keyof TenantPermissionActions) => boolean;
  hasTenantFeature: (feature: PlanFeature) => boolean;
  getSecurityStatus: () => Promise<any>;

  // Utility
  clearError: () => void;
  checkAuthStatus: () => void;
}

// Combined Hook Return Type
export interface UseApiClientReturn extends ApiClientState, ApiClientActions {}

/**
 * Multi-Tenant API Client Hook
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const {
 *     isLoading,
 *     user,
 *     tenantContext,
 *     login,
 *     hasPermission
 *   } = useApiClient();
 *
 *   const canManageUsers = hasPermission('users', 'write');
 *
 *   return (
 *     <div>
 *       {user && <p>Welcome, {user.name}</p>}
 *       {canManageUsers && <UserManagementPanel />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useApiClient(): UseApiClientReturn {
  // ====================================
  // Error Context Integration (Safe)
  // ====================================

  // Safe useErrorContext at top level with error boundary handling
  const errorContext = useErrorContext();

  const reportError = errorContext.reportError;

  // ====================================
  // State Management
  // ====================================

  const [state, setState] = useState<ApiClientState>(() => ({
    isLoading: false,
    isAuthenticated: AuthService.isAuthenticated(),
    user: AuthService.getCurrentUserV2(),
    tenantContext: AuthService.getCurrentTenantContextV2(),
    error: null,
  }));

  // ====================================
  // Utility Functions
  // ====================================

  const updateState = useCallback((updates: Partial<ApiClientState>) => {
    setState(prevState => ({ ...prevState, ...updates }));
  }, []);

  const handleApiCall = useCallback(async <T>(
    apiCall: () => Promise<T>,
    onSuccess?: (result: T) => void,
    onError?: (error: EnhancedApiError) => void
  ): Promise<T | null> => {
    updateState({ isLoading: true, error: null });

    try {
      const result = await apiCall();
      onSuccess?.(result);
      updateState({ isLoading: false });
      return result;
    } catch (error) {
      const apiError = handleError(error);

      // Report error to global error context (with safe fallback)
      const errorId = reportError(apiError, {
        source: 'useApiClient',
        operation: 'api_call',
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      });

      updateState({
        isLoading: false,
        error: apiError,
        // If authentication failed, update auth state
        isAuthenticated: apiError.statusCode !== 401 ? state.isAuthenticated : false,
        user: apiError.statusCode === 401 ? null : state.user,
        tenantContext: apiError.statusCode === 401 ? null : state.tenantContext,
      });

      logger.error('[useApiClient] API call failed:', { errorId, error: apiError });
      onError?.(apiError);
      return null;
    }
  }, [state.isAuthenticated, state.user, state.tenantContext, updateState, reportError]);

  // ====================================
  // Authentication Actions
  // ====================================

  const login = useCallback(async (
    email: string,
    password: string,
    tenantSubdomain?: string
  ) => {
    await handleApiCall(
      () => AuthService.loginV2({
        email,
        password,
        tenantSubdomain,
        deviceCharacteristics: {
          userAgent: navigator.userAgent,
          screen: {
            width: screen.width,
            height: screen.height,
            colorDepth: screen.colorDepth,
          },
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          language: navigator.language,
          platform: navigator.platform,
          cookiesEnabled: navigator.cookieEnabled,
          doNotTrack: navigator.doNotTrack === '1',
        },
      }),
      (response) => {
        AuthService.saveV2LoginResponse(response);
        updateState({
          isAuthenticated: true,
          user: response.user,
          tenantContext: AuthService.getCurrentTenantContextV2(),
        });
        logger.log('[useApiClient] Login successful');
      }
    );
  }, [handleApiCall, updateState]);

  const logout = useCallback(async () => {
    await handleApiCall(
      () => AuthService.logout(),
      () => {
        updateState({
          isAuthenticated: false,
          user: null,
          tenantContext: null,
        });
        logger.log('[useApiClient] Logout successful');
      }
    );
  }, [handleApiCall, updateState]);

  const refreshToken = useCallback(async () => {
    await handleApiCall(
      () => AuthService.refreshToken(),
      () => {
        updateState({
          isAuthenticated: true,
          user: AuthService.getCurrentUserV2(),
          tenantContext: AuthService.getCurrentTenantContextV2(),
        });
        logger.log('[useApiClient] Token refreshed successfully');
      }
    );
  }, [handleApiCall, updateState]);

  const switchTenant = useCallback(async (tenantId: string) => {
    await handleApiCall(
      () => AuthService.switchTenantV2({ tenantId }),
      (response) => {
        updateState({
          user: response.user,
          tenantContext: AuthService.getCurrentTenantContextV2(),
        });
        logger.log('[useApiClient] Tenant switched successfully');
      }
    );
  }, [handleApiCall, updateState]);

  // ====================================
  // User Management Actions
  // ====================================

  const getCurrentUser = useCallback((): UserInfo | null => {
    return AuthService.getCurrentUserV2();
  }, []);

  const updateProfile = useCallback(async (data: Partial<UserInfo>) => {
    const currentUser = state.user;
    if (!currentUser) throw new Error('No authenticated user');

    await handleApiCall(
      () => AuthService.updateUser(currentUser.id, data),
      () => {
        updateState({
          user: AuthService.getCurrentUserV2(),
        });
        logger.log('[useApiClient] Profile updated successfully');
      }
    );
  }, [state.user, handleApiCall, updateState]);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const currentUser = state.user;
    if (!currentUser) throw new Error('No authenticated user');

    await handleApiCall(
      () => AuthService.changeUserPassword(currentUser.id, { currentPassword, newPassword }),
      () => {
        logger.log('[useApiClient] Password changed successfully');
      }
    );
  }, [state.user, handleApiCall]);

  // ====================================
  // Tenant Operations
  // ====================================

  const getCurrentTenant = useCallback(async (): Promise<TenantInfo | null> => {
    const result = await handleApiCall(() => AuthService.getCurrentTenantV2());
    return result as TenantInfo | null;
  }, [handleApiCall]);

  const updateTenantSettings = useCallback(async (settings: unknown) => {
    const tenantContext = state.tenantContext;
    if (!tenantContext) throw new Error('No tenant context');

    await handleApiCall(
      () => AuthService.updateTenantSettings(tenantContext.id, settings),
      () => {
        logger.log('[useApiClient] Tenant settings updated successfully');
      }
    );
  }, [state.tenantContext, handleApiCall]);

  const getTenantAnalytics = useCallback(async () => {
    const tenantContext = state.tenantContext;
    if (!tenantContext) throw new Error('No tenant context');

    return await handleApiCall(() => AuthService.getTenantAnalytics(tenantContext.id));
  }, [state.tenantContext, handleApiCall]);

  // ====================================
  // Permission & Security Actions
  // ====================================

  const hasPermission = useCallback((
    resource: TenantResource,
    action: keyof TenantPermissionActions
  ): boolean => {
    return AuthService.hasV2Permission(resource, action);
  }, []);

  const hasTenantFeature = useCallback((feature: PlanFeature): boolean => {
    return AuthService.hasTenantFeature(feature);
  }, []);

  const getSecurityStatus = useCallback(async () => {
    return await handleApiCall(() => AuthService.getSecurityStatus());
  }, [handleApiCall]);

  // ====================================
  // Utility Actions
  // ====================================

  const clearError = useCallback(() => {
    updateState({ error: null });
  }, [updateState]);

  const checkAuthStatus = useCallback(() => {
    const isAuth = AuthService.isAuthenticated();
    const user = AuthService.getCurrentUserV2();
    const tenantContext = AuthService.getCurrentTenantContextV2();

    updateState({
      isAuthenticated: isAuth,
      user,
      tenantContext,
    });
  }, [updateState]);

  // ====================================
  // Memoized Return Object
  // ====================================

  const apiClient = useMemo((): UseApiClientReturn => ({
    // State
    ...state,

    // Authentication Actions
    login,
    logout,
    refreshToken,
    switchTenant,

    // User Management Actions
    getCurrentUser,
    updateProfile,
    changePassword,

    // Tenant Operations
    getCurrentTenant,
    updateTenantSettings,
    getTenantAnalytics,

    // Permission & Security Actions
    hasPermission,
    hasTenantFeature,
    getSecurityStatus,

    // Utility Actions
    clearError,
    checkAuthStatus,
  }), [
    state,
    login,
    logout,
    refreshToken,
    switchTenant,
    getCurrentUser,
    updateProfile,
    changePassword,
    getCurrentTenant,
    updateTenantSettings,
    getTenantAnalytics,
    hasPermission,
    hasTenantFeature,
    getSecurityStatus,
    clearError,
    checkAuthStatus,
  ]);

  return apiClient;
}

export default useApiClient;

// ====================================
// Helper Hooks
// ====================================

/**
 * Hook for checking specific permissions
 */
export function usePermission(resource: TenantResource, action: keyof TenantPermissionActions) {
  const { hasPermission } = useApiClient();
  return useMemo(() => hasPermission(resource, action), [hasPermission, resource, action]);
}

/**
 * Hook for checking tenant features
 */
export function useTenantFeature(feature: PlanFeature) {
  const { hasTenantFeature } = useApiClient();
  return useMemo(() => hasTenantFeature(feature), [hasTenantFeature, feature]);
}

/**
 * Hook for current user information
 */
export function useCurrentUser() {
  const { user, isAuthenticated } = useApiClient();
  return useMemo(() => ({ user, isAuthenticated }), [user, isAuthenticated]);
}

/**
 * Hook for current tenant context
 */
export function useCurrentTenant() {
  const { tenantContext } = useApiClient();
  return useMemo(() => tenantContext, [tenantContext]);
}